/**
 * StudioBackend FileServerRetouchFileNameParser ile uyumlu (frameInfo çıkarımı).
 */

const PREFIX_PATTERN = /^(\d{5})-(\w{8})-(\w{3})_(\d{8,13})/;
const LEGACY_TAIL_PATTERN = /^_?[A-Za-z0-9]*$/;
const EXTENDED_TAIL_PATTERN = /^((?:_AI|_[A-Za-z0-9]+|-\d+))+?$/;
const UNIQUE_NUMBER_PATTERN = /^_\d+/;

export interface RetouchFileNameParseResult {
    success: boolean;
    frameInfo: string | null;
    errorCode?: 'empty_filename' | 'filename_pattern_mismatch' | 'invalid_barcode' | 'missing_frame_info';
}

function isAllDigitsSeg(seg: string): boolean {
    return seg.length > 0 && /^\d+$/.test(seg);
}

function isAiNameSegment(seg: string): boolean {
    return seg.toUpperCase() === 'AI';
}

function isFrameCodeSegment(seg: string): boolean {
    if (!seg || isAiNameSegment(seg)) {
        return false;
    }

    if (/^[A-Za-z]/.test(seg)) {
        return /^[A-Za-z0-9]+$/.test(seg);
    }

    if (/^\d/.test(seg) && seg.length <= 2) {
        return /^[A-Za-z0-9]+$/.test(seg);
    }

    return false;
}

function isValidTail(rest: string): boolean {
    return LEGACY_TAIL_PATTERN.test(rest) || EXTENDED_TAIL_PATTERN.test(rest);
}

function normalizeChildMeasureUnderscoreSeparator(rest: string): string {
    if (!rest || rest[0] !== '-') {
        return rest;
    }

    const m = rest.match(/^-(\d+)_(\d+)(_.*)?$/);
    if (!m) {
        return rest;
    }

    const tail = m[3] ?? '';
    return `-${m[1]}-${m[2]}${tail}`;
}

function stripUniquenessBetweenMeasuresAndFrame(rest: string): string {
    if (!rest || rest[0] !== '-') {
        return rest;
    }

    const m = rest.match(/^(-\d+(?:-\d+)*)_(\d+)((?:_[A-Za-z].*)?)$/);
    if (!m) {
        return rest;
    }

    const measurePart = m[1];
    const segmentCount = measurePart.replace(/^-/, '').split('-').filter(Boolean).length;
    if (segmentCount !== 2 && segmentCount !== 4 && segmentCount !== 5) {
        return rest;
    }

    const digitPart = m[2];
    const frameTail = m[3] ?? '';

    if (!frameTail && isFrameCodeSegment(digitPart)) {
        return rest;
    }

    return `${measurePart}${frameTail}`;
}

function isNumericDashSegmentBlock(value: string): boolean {
    if (!value) {
        return false;
    }

    return value.split('-').filter(Boolean).every((seg) => isAllDigitsSeg(seg));
}

function tryParseMeasureSegments(rest: string): { ok: boolean; segments: string[] | null } {
    if (!rest) {
        return { ok: true, segments: null };
    }

    if (rest[0] === '_') {
        return { ok: isValidTail(rest), segments: null };
    }

    const underscore = rest.indexOf('_');
    let measurePart: string;
    let tailPart: string;

    if (underscore < 0) {
        if (!isNumericDashSegmentBlock(rest)) {
            return { ok: false, segments: null };
        }
        measurePart = rest;
        tailPart = '';
    } else {
        measurePart = rest.slice(0, underscore);
        tailPart = rest.slice(underscore);
        if (!isValidTail(tailPart)) {
            return { ok: false, segments: null };
        }
    }

    if (!measurePart) {
        return { ok: true, segments: null };
    }

    if (!measurePart.startsWith('-') || !isNumericDashSegmentBlock(measurePart)) {
        return { ok: false, segments: null };
    }

    return {
        ok: true,
        segments: measurePart.replace(/^-/, '').split('-').filter(Boolean),
    };
}

function getTailFromRest(rest: string): string {
    if (!rest) {
        return '';
    }

    if (rest[0] === '_') {
        return rest;
    }

    const underscore = rest.indexOf('_');
    return underscore < 0 ? '' : rest.slice(underscore);
}

function tryExtractExtendedFrameInfo(rest: string): string | null {
    for (const seg of rest.split(/[_-]/).filter(Boolean)) {
        if (isFrameCodeSegment(seg)) {
            return seg;
        }
        if (isAllDigitsSeg(seg) || isAiNameSegment(seg)) {
            continue;
        }
    }
    return null;
}

function buildFrameInfo(rest: string): string | null {
    if (!rest) {
        return null;
    }

    if (LEGACY_TAIL_PATTERN.test(rest)) {
        const trimmed = rest.replace(/^_/, '');
        return trimmed.length > 0 ? trimmed : null;
    }

    return tryExtractExtendedFrameInfo(rest);
}

function getFileNameWithoutExtension(fileName: string): string {
    const trimmed = (fileName || '').trim().replace(/\\/g, '/');
    const name = trimmed.includes('/') ? trimmed.slice(trimmed.lastIndexOf('/') + 1) : trimmed;
    const lastDot = name.lastIndexOf('.');
    return lastDot > 0 ? name.slice(0, lastDot) : name;
}

export function parseRetouchFileName(fileName: string, minBarcodeDigits = 8): RetouchFileNameParseResult {
    const name = getFileNameWithoutExtension(fileName);
    if (!name) {
        return { success: false, frameInfo: null, errorCode: 'empty_filename' };
    }

    const prefixMatch = name.match(PREFIX_PATTERN);
    if (!prefixMatch) {
        return { success: false, frameInfo: null, errorCode: 'filename_pattern_mismatch' };
    }

    let rest = name.slice(prefixMatch[0].length);

    const uniqueMatch = rest.match(UNIQUE_NUMBER_PATTERN);
    if (uniqueMatch) {
        const digitPart = uniqueMatch[0].slice(1);
        const afterUnique = rest.slice(uniqueMatch[0].length);
        const isFrameOnlySuffix = afterUnique.length === 0 && isFrameCodeSegment(digitPart);
        if (!isFrameOnlySuffix) {
            rest = afterUnique;
        }
    }

    if (rest.startsWith('_AI-') || rest.startsWith('_ai-')) {
        rest = rest.slice(3);
    } else if (rest.toUpperCase() === '_AI') {
        rest = '';
    }

    rest = normalizeChildMeasureUnderscoreSeparator(rest);
    rest = stripUniquenessBetweenMeasuresAndFrame(rest);

    const barcodeStr = prefixMatch[4];
    if (barcodeStr.length < minBarcodeDigits || !/^\d+$/.test(barcodeStr)) {
        return { success: false, frameInfo: null, errorCode: 'invalid_barcode' };
    }

    const measureParse = tryParseMeasureSegments(rest);
    if (!measureParse.ok) {
        return { success: false, frameInfo: null, errorCode: 'filename_pattern_mismatch' };
    }

    const segmentCount = measureParse.segments?.length ?? 0;
    if (segmentCount !== 0 && segmentCount !== 2 && segmentCount !== 4 && segmentCount !== 5) {
        return { success: false, frameInfo: null, errorCode: 'filename_pattern_mismatch' };
    }

    const tail = getTailFromRest(rest);
    const frameInfo = tail ? buildFrameInfo(tail) : null;

    if (!frameInfo) {
        return { success: false, frameInfo: null, errorCode: 'missing_frame_info' };
    }

    return { success: true, frameInfo };
}
